import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CancelIcon from "@mui/icons-material/Cancel";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import theme from "../../theme";
import { addSubscriptionSchema } from "../../schemas/clientSchema";
import SubscriptionFormFields from "./clientProfile/SubscriptionFormFields";

/**
 * Editable subscription chip with expandable form fields
 * Used in Add Client modal to allow customization of subscriptions before onboarding
 */
const SubscriptionChipEditor = ({
  subscription,
  onRemove,
  onChange,
  initialValues = {},
  hasEwayCredentials = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate default dates
  const today = dayjs();
  const defaultStartDate = initialValues.startDate || today.format("YYYY-MM-DD");
  const paymentDelayDays = subscription.payment_delay_days || 0;
  const defaultNextPaymentDate =
    initialValues.nextPaymentDate || today.add(paymentDelayDays, "day").format("YYYY-MM-DD");

  // Use payment_category_id (UUID) instead of payment_category (name string)
  // Ensure we don't accidentally use subscription.id or any invalid value
  const paymentCategoryValue = initialValues.paymentCategory || subscription.payment_category_id || "";

  // Determine default payment method/type based on eWay credentials
  const getDefaultPaymentMethod = () => {
    const subMethod = initialValues.paymentMethods || subscription.payment_method;
    // If subscription wants automatic but no eWay credentials, fall back to manual
    if (subMethod === "automatic" && !hasEwayCredentials) {
      return "manual";
    }
    return subMethod || (hasEwayCredentials ? "automatic" : "manual");
  };

  const getDefaultPaymentType = () => {
    const subType = initialValues.paymentTypes || subscription.payment_type;
    // If subscription wants eway but no credentials, fall back to cash
    if (subType === "eway" && !hasEwayCredentials) {
      return "cash";
    }
    return subType || (hasEwayCredentials ? "eway" : "cash");
  };

  const {
    register,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addSubscriptionSchema),
    defaultValues: {
      subscriptionId: subscription.id,
      startDate: defaultStartDate,
      nextPaymentDate: defaultNextPaymentDate,
      paymentTermNumber: initialValues.paymentTermNumber || subscription.payment_term_number || 1,
      paymentTermFrequency: initialValues.paymentTermFrequency || subscription.payment_term_frequency || "",
      paymentAmount: initialValues.paymentAmount || subscription.payment_amount || 0,
      paymentFrequency: initialValues.paymentFrequency || subscription.payment_frequency || "",
      paymentCategory: paymentCategoryValue,
      sessionsNumber: initialValues.sessionsNumber || subscription.sessions_number || 1,
      sessionsFrequency: initialValues.sessionsFrequency || subscription.sessions_frequency || "",
      paymentMethods: getDefaultPaymentMethod(),
      paymentTypes: getDefaultPaymentType(),
    },
  });

  // Watch specific form fields instead of entire formValues object to prevent infinite loops
  const startDate = watch("startDate");
  const nextPaymentDate = watch("nextPaymentDate");
  const paymentTermNumber = watch("paymentTermNumber");
  const paymentTermFrequency = watch("paymentTermFrequency");
  const paymentAmount = watch("paymentAmount");
  const paymentFrequency = watch("paymentFrequency");
  const paymentCategory = watch("paymentCategory");
  const sessionsNumber = watch("sessionsNumber");
  const sessionsFrequency = watch("sessionsFrequency");
  const paymentMethods = watch("paymentMethods");
  const paymentTypes = watch("paymentTypes");

  // Use ref to store the onChange callback to avoid it being a dependency
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Update payment method/type when hasEwayCredentials changes
  useEffect(() => {
    const currentMethod = getValues("paymentMethods");
    const currentType = getValues("paymentTypes");

    if (!hasEwayCredentials) {
      // No eWay credentials - reset automatic to manual and eway to cash
      if (currentMethod === "automatic") {
        setValue("paymentMethods", "manual");
      }
      if (currentType === "eway") {
        setValue("paymentTypes", "cash");
      }
    }
  }, [hasEwayCredentials, getValues, setValue]);

  // Emit changes to parent whenever form values change
  useEffect(() => {
    onChangeRef.current(subscription.id, {
      subscription_id: subscription.id,
      start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      next_payment_date: nextPaymentDate ? dayjs(nextPaymentDate).format("YYYY-MM-DD") : null,
      payment_term_number: paymentTermNumber,
      payment_term_frequency: paymentTermFrequency,
      payment_amount: paymentAmount,
      payment_frequency: paymentFrequency,
      payment_category: paymentCategory,
      sessions_number: sessionsNumber,
      sessions_frequency: sessionsFrequency,
      payment_method: paymentMethods,
      payment_type: paymentTypes,
    });
  }, [
    subscription.id,
    startDate,
    nextPaymentDate,
    paymentTermNumber,
    paymentTermFrequency,
    paymentAmount,
    paymentFrequency,
    paymentCategory,
    sessionsNumber,
    sessionsFrequency,
    paymentMethods,
    paymentTypes,
  ]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: "12px",
        mb: 2,
        overflow: "hidden",
      }}
    >
      {/* Header with subscription name and controls */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          p: 1.5,
          backgroundColor: isExpanded ? theme.palette.primary.light : theme.palette.white.light,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: isExpanded ? theme.palette.primary.light : theme.palette.grey[100],
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Typography fontWeight={500} fontSize={16} sx={{ color: isExpanded ? "#fff" : "inherit" }}>
            {subscription.name}
          </Typography>
          {!isExpanded && paymentAmount > 0 && (
            <Typography fontSize={14} color={theme.palette.grey[600]}>
              (${paymentAmount}/{paymentFrequency || "month"})
            </Typography>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small"
            sx={{ color: isExpanded ? "#fff" : "inherit" }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: isExpanded ? "#fff" : "inherit" }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(subscription.id);
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Expandable form fields */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, backgroundColor: theme.palette.white.main }}>
          <Stack spacing={2}>
            <SubscriptionFormFields
              register={register}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              errors={errors}
              startDateLabel="Start Date"
              hasEwayCredentials={hasEwayCredentials}
            />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

SubscriptionChipEditor.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    payment_term_number: PropTypes.number,
    payment_term_frequency: PropTypes.string,
    payment_amount: PropTypes.number,
    payment_frequency: PropTypes.string,
    payment_category: PropTypes.string,
    payment_category_id: PropTypes.string,
    sessions_number: PropTypes.number,
    sessions_frequency: PropTypes.string,
    payment_delay_days: PropTypes.number,
    payment_method: PropTypes.string,
    payment_type: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  hasEwayCredentials: PropTypes.bool,
};

export default SubscriptionChipEditor;
